/* eslint-disable no-case-declarations */
import customStatusReducer from 'containers/Settings/Leads/reducers';
import {
  GET_LEADS_STATUSES,
  GET_LEADS_STATUSES_SUCCESS,
  CREATE_LEADS_STATUS,
  UPDATE_STATUSES_SUCCESS,
  UPDATE_STATUSES_FAIL,
  REORDER_LEADS_STATUSES,
  REORDER_LEADS_STATUSES_SUCCESS,
  REORDER_LEADS_STATUSES_FAIL,
} from 'containers/Settings/Leads/types';
import { transformGqlDataToRecord } from 'pages/Leads/LeadRecord/utils/transforms';
import customFieldsReducer from './Lead/customFieldsReducers';
import customLeadRecordReducer from './Lead/reducers';
import * as types from './types';

const initialState = {
  preferences: [],
  filters: [],
  orgMembers: [],
  leads: {},
  leadsById: {},
  leadCustomFields: {},
  leadsLoading: false,
  firstLoadComplete: false,
  firstFiltersLoadComplete: false,
  error: null,
  leadAssignments: {},
};

// Merges `next` over `prev`, ignoring keys whose value is undefined. GraphQL queries
// are partial projections of a lead, so a field the query doesn't select must not blank
// out a value the REST fetch already populated.
const mergeDefined = (prev = {}, next = {}) => Object.entries(next).reduce(
  (acc, [key, value]) => (value === undefined ? acc : { ...acc, [key]: value }),
  { ...prev },
);

// The nested `lead` object needs merging one level down; a shallow spread would replace
// it wholesale and drop every field the projection doesn't carry.
const mergeLeadRecord = (prev = {}, next = {}) => ({
  ...mergeDefined(prev, next),
  lead: mergeDefined(prev.lead, next.lead),
});

export default (state = initialState, action) => {

  const leadCustomFields = customFieldsReducer(state.leadCustomFields, action);
  const leadsById = customLeadRecordReducer(state.leadsById, action);
  // Feed the lead-record reducer's result into leadsReducer rather than overwriting
  // leadsReducer's output with it, so both can update leadsById.
  const newState = leadsReducer({ ...state, leadsById }, action);
  return {
    ...newState,
    leadCustomFields,
  };
};

const leadsReducer = (state = initialState, action) => {
  let items = [];

  switch (action.type) {
    case GET_LEADS_STATUSES:
      // Falls through.
    case CREATE_LEADS_STATUS:
      // Falls through.
    case GET_LEADS_STATUSES_SUCCESS:
      // Falls through.
    case UPDATE_STATUSES_SUCCESS:
      // Falls through.
    case UPDATE_STATUSES_FAIL:
      // Falls through.
    case REORDER_LEADS_STATUSES:
      // Falls through.
    case REORDER_LEADS_STATUSES_SUCCESS:
      // Falls through.
    case REORDER_LEADS_STATUSES_FAIL:
      return { ...state, filters: customStatusReducer(state.filters, action) };
    case types.GET_LEADS_FILTERS:
      return { ...state, firstFiltersLoadComplete: false };
    case types.GET_LEADS_FILTERS_IN_BACKGROUND:
      return state;
    case types.GET_LEADS_FILTERS_SUCCESS:
      return {
        ...state,
        filters: action.payload,
        firstFiltersLoadComplete: true,
      };
    case types.GET_LEADS_FILTERS_FAIL:
      return state;
    case types.GET_LEADS:
      return {
        ...state,
        leadsLoading: true,
        error: null,
      };
    case types.GET_SINGLE_LEAD_SUCCESS: {
      const lead = action?.payload;
      if (!lead) return state;
      const { id: _id } = lead;
      return {
        ...state,
        leadsById: {
          ...state.leadsById,
          [_id]: mergeLeadRecord(state.leadsById[_id], transformGqlDataToRecord(lead)),
        },
      };
    };
    case types.GET_LEADS_SUCCESS: {
      const { items: _items } = action.payload;
      const nextLeadsById = { ...state.leadsById };
      (_items || []).forEach(item => {
        if (!item) return;
        const { id: _id } = item;
        nextLeadsById[_id] = mergeLeadRecord(
          nextLeadsById[_id],
          transformGqlDataToRecord(item),
        );
      });
      return {
        ...state,
        leads: action.payload,
        leadsLoading: false,
        firstLoadComplete: true,
        leadsById: nextLeadsById,
      };
    }
    case types.GET_LEADS_FAIL:
      return {
        ...state,
        leadsLoading: false,
      };
    case types.SEARCH_LEADS:
      return {
        ...state,
        leadsLoading: true,
        error: null,
      };
    case types.SEARCH_LEADS_SUCCESS:
      return {
        ...state,
        leads: action.payload,
        leadsLoading: false,
      };
    case types.SEARCH_LEADS_FAIL:
      return {
        ...state,
        leadsLoading: false,
      };
    case types.UPDATE_LEAD_STATUS:
      return {
        ...state,
        error: null,
      };
    case types.UPDATE_LEAD_SUCCESS:
      // #TODO: Possibly refactor this to handle the many different scenarios of updating leads
      // by including the whole leads schema in the response payload.
      const { leadId, next, prev, lastContacted } = action.payload;
      const newLeads = state.leads;
      let newFilters = null;
      // This checks which kind of lead update is happening: status change or
      // last contacted date change.
      if (next && prev) {
        items = newLeads?.items?.map(el => {
          if (el.id === leadId) return { ...el, leadCustomStatusId: next };
          return el;
        });
        newFilters = state.filters.map(el => {
          if (el.id === next) return { ...el, count: el.count + 1 };
          if (el.id === prev) return { ...el, count: el.count > 0 ? el.count - 1 : 0 };
          return el;
        });
      }
      if (lastContacted) {
        items = newLeads?.items?.map(el => {
          if (el.id === leadId) return { ...el, lastContactedDate: lastContacted };
          return el;
        });
      }
      return {
        ...state,
        leads: {
          ...newLeads,
          items,
        },
        ...(newFilters && { filters: newFilters }),
      };
    case types.UPDATE_LEAD_ERROR:
      return {
        ...state,
        error: action.payload,
      };
    case types.UPDATE_LEAD_CANCEL:
      return {
        ...state,
        error: null,
      };
    case types.DELETE_LEAD_SUCCESS:
      const { deleteId } = action.payload;
      let deleteStatus = null;
      items = state.leads?.items?.filter(el => {
        if (el.id === deleteId) {
          deleteStatus = el.leadCustomStatusId;
          return false;
        }
        return true;
      });
      const filters1 = state.filters.map(el => {
        if (el.id === deleteStatus) return { ...el, count: el.count - 1 };
        if (el.id === 'all') return { ...el, count: el.count - 1 };
        if (el.id === 'current') return { ...el, count: el.count - 1 };
        return el;
      });
      return {
        ...state,
        leads: {
          ...state.leads,
          items,
        },
        filters: filters1,
      };
    case types.DELETE_LEAD_ERROR:
      return {
        ...state,
        error: action.payload,
      };
    case types.DELETE_LEAD_CANCEL:
      return {
        ...state,
        error: null,
      };
    case types.GET_ORG_MEMBERS:
      return {
        ...state,
        error: null,
      };
    case types.GET_ORG_MEMBERS_SUCCESS:
      return {
        ...state,
        orgMembers: action.payload,
        error: null,
      };
    case types.GET_ORG_MEMBERS_FAIL:
      return {
        ...state,
        error: action.payload,
      };
    case types.CREATE_LEAD_ASSIGNMENT_SUCCESS:
      const { leadId: leadIdAssignment, loginId, name } = action.payload;
      const updatedLeads = state.leads;
      items = updatedLeads?.items?.map(el => {
        if (el.id === leadIdAssignment) {
          return { ...el, organizationMemberAssignments: [...el.organizationMemberAssignments, { loginId, name }] };
        }
        return el;
      });
      return {
        ...state,
        leads: {
          ...updatedLeads,
          items,
        },
        error: null,
      };
    case types.CREATE_LEAD_ASSIGNMENT_FAIL:
      return {
        ...state,
        error: true,
      };
    case types.DELETE_LEAD_ASSIGNMENT_SUCCESS:
      const { leadId: leadDeleteId, loginId: leadDeleteLoginId } = action.payload;
      const updatedLeadsDeleteAssignment = state.leads;
      items = updatedLeadsDeleteAssignment?.items?.map(el => {
        if (el.id === leadDeleteId) {
          const newAssignments =
            el.organizationMemberAssignments.filter(assignment => assignment.loginId !== leadDeleteLoginId);
          return { ...el, organizationMemberAssignments: newAssignments };
        }
        return el;
      });
      return {
        ...state,
        leads: {
          ...updatedLeadsDeleteAssignment,
          items,
        },
        error: null,
      };
    case types.DELETE_LEAD_ASSIGNMENT_FAIL:
      return {
        ...state,
        error: action.payload,
      };
    case types.UPDATE_LEAD_ASSIGNMENTS_STATE:
      const newAssignments = {
        ...state.leadAssignments,
        [action.payload.leadId]: action.payload.data,
      };
      return {
        ...state,
        leadAssignments: newAssignments,
      };
    case types.ADD_LEAD_CUSTOM_PREFERENCES:
      return {
        ...state,
        preferences: action.payload.data.leadCustomPreferences,
      };
    case types.DUPLICATE_LEAD_SUCCESS: {
      const { lead } = action.payload;
      if (!lead) return state;
      const filtersAfterDuplicate = state.filters.map(el => {
        if (el.id === lead.leadCustomStatusId) return { ...el, count: el.count + 1 };
        if (el.id === 'all') return { ...el, count: el.count + 1 };
        if (el.id === 'current') return { ...el, count: el.count + 1 };
        return el;
      });
      return {
        ...state,
        leads: {
          ...state.leads,
          items: [lead, ...(state.leads?.items || [])],
        },
        filters: filtersAfterDuplicate,
      };
    }
    default:
      return state;
  }
};
